import { API } from "@swivl-mono/api-helper";
import {
  MemberInterface,
  RoutingGroupInterface,
  TicketPriority,
  TicketTemplate,
  TicketTemplateDefinition,
  formatTicketNumberDisplay
} from "@swivl-mono/types";
import { SelectItem } from "@swivl-mono/ui";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SelectedCustomer } from "../../../Components/CustomerPickerModal";
import { formatUserDisplayName } from "../../../Helpers/Formatting/formatUserDisplayName";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";
import MembersModel from "../../../Models/Member/Members.Model";
import { CreatedTicketResult, useTicketingStore } from "../../../Models/Ticketing/Ticketing.Store";
import { buildAssigneeOptions, getMemberDisplayName, memberCanAccessTicketLocation } from "./assigneeLocationScope";

export interface TicketTask {
  id: string;
  description: string;
}

// Re-exported for the ticketing views that already import it from here.
export { getMemberDisplayName };

export interface UseCreateTicketOutput {
  // Form state
  selectedTemplateId: number | null;
  title: string;
  setTitle: Dispatch<SetStateAction<string>>;
  description: string;
  setDescription: Dispatch<SetStateAction<string>>;
  assigneeMemberIds: number[];
  setAssigneeMemberIds: Dispatch<SetStateAction<number[]>>;
  locationId: number | null;
  setLocationId: Dispatch<SetStateAction<number | null>>;
  priority: TicketPriority;
  setPriority: Dispatch<SetStateAction<TicketPriority>>;
  selectedCustomer: SelectedCustomer | null;
  setSelectedCustomer: Dispatch<SetStateAction<SelectedCustomer | null>>;
  tags: string;
  setTags: Dispatch<SetStateAction<string>>;
  routingGroupSlug: string | null;
  setRoutingGroupSlug: Dispatch<SetStateAction<string | null>>;

  // Tasks
  tasks: TicketTask[];
  setTasks: Dispatch<SetStateAction<TicketTask[]>>;
  newTaskText: string;
  setNewTaskText: Dispatch<SetStateAction<string>>;
  showTaskInput: boolean;
  setShowTaskInput: Dispatch<SetStateAction<boolean>>;

  // Status
  submitting: boolean;
  error: string | null;

  // Data
  templates: TicketTemplate[];
  templatesLoading: boolean;
  selectedTemplate: TicketTemplate | null;

  // Derived options
  assigneeOptions: SelectItem[];
  locationOptions: SelectItem[];
  groupOptions: SelectItem[];

  // Actions
  handleTemplateChange: (templateId: number | null) => void;
  handleSubmit: () => Promise<void>;
}

/** Optional initial form values — used by the CC right column's LLM-prefilled Create Ticket (ENG-2957 §9.4). */
export interface CreateTicketSeed {
  title?: string;
  description?: string;
  /** Comma-separated, same format the tags input holds. */
  tags?: string;
  customer?: SelectedCustomer;
}

export function useCreateTicket(
  onClose: () => void,
  onCreated?: (ticket: CreatedTicketResult) => void,
  seed?: CreateTicketSeed
): UseCreateTicketOutput {
  const navigate = useNavigate();
  const createTicket = useTicketingStore((s) => s.createTicket);
  const loadTemplates = useTicketingStore((s) => s.loadTemplates);
  const templates = useTicketingStore((s) => s.templates);
  const templatesLoading = useTicketingStore((s) => s.templatesLoading);
  const defaultTemplateId = useTicketingStore((s) => s.defaultTemplateId);
  const templatesLoaded = useTicketingStore((s) => s.templatesLoaded);

  // Form state (seed values apply on mount — the modal opens after any prefill fetch)
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [title, setTitle] = useState(seed?.title ?? "");
  const [description, setDescription] = useState(seed?.description ?? "");
  const [assigneeMemberIds, setAssigneeMemberIds] = useState<number[]>([]);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [priority, setPriority] = useState<TicketPriority>(TicketPriority.NONE);
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(seed?.customer ?? null);
  const [tags, setTags] = useState(seed?.tags ?? "");
  // Target routing group slug — dispatches the new ticket to this group's inbox.
  const [routingGroupSlug, setRoutingGroupSlug] = useState<string | null>(null);

  const [tasks, setTasks] = useState<TicketTask[]>([]);
  const [newTaskText, setNewTaskText] = useState("");
  const [showTaskInput, setShowTaskInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Data
  const [members, setMembers] = useState<MemberInterface[]>([]);
  const [routingGroups, setRoutingGroups] = useState<RoutingGroupInterface[]>([]);
  const { groups } = LocationsModel.useState((s) => s);

  // Load templates and data on mount
  useEffect(() => {
    void loadTemplates();
    void MembersModel.loadMembers();
    void LocationsModel.getGroupsAndLocations(false);
  }, [loadTemplates]);

  // Load the org's routing groups for the Group picker.
  useEffect(() => {
    let active = true;
    void API.RoutingGroup.listGroups(Member.orgId)
      .then((rows) => {
        if (active) setRoutingGroups(rows);
      })
      .catch(() => {
        if (active) setRoutingGroups([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // Sync members from MembersModel
  useEffect(() => {
    if (MembersModel.state.members?.length) {
      setMembers(MembersModel.state.members);
    }
  }, []);

  // Poll for members if not loaded
  useEffect(() => {
    const interval = setInterval(() => {
      if (MembersModel.state.members?.length && members.length === 0) {
        setMembers(MembersModel.state.members);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [members.length]);

  // Auto-select the default template once templates are loaded. It FILLS, never replaces:
  // the form may already carry the conversation's prefill (CC v3's Create Ticket seeds the
  // customer, title, description and tags) or what the agent typed while the list was still
  // loading, and the template used to overwrite all of it, blank template fields included
  // (ENG-3790 fix window, D9).
  useEffect(() => {
    if (!initialized && templatesLoaded) {
      if (defaultTemplateId) {
        const defaultTemplate = templates.find((t) => t.id === defaultTemplateId);
        if (defaultTemplate) {
          applyTemplate(defaultTemplate, "fill");
          setSelectedTemplateId(defaultTemplateId);
        }
      }
      setInitialized(true);
    }
  }, [initialized, templatesLoaded, templates, defaultTemplateId]);

  /** Anything on the form the agent (or a prefill) put there — what a template must not silently discard. */
  const hasEntries =
    title.trim() !== "" ||
    description.trim() !== "" ||
    tags.trim() !== "" ||
    assigneeMemberIds.length > 0 ||
    tasks.length > 0 ||
    selectedCustomer !== null ||
    locationId !== null ||
    priority !== TicketPriority.NONE;

  /**
   * `replace`: the template is the form (an explicit pick from the dropdown). `fill`: the template
   * supplies only the fields still empty, so a seeded or typed value always wins.
   */
  const applyTemplate = (template: TicketTemplate | null, mode: "replace" | "fill"): void => {
    const keep = mode === "fill";
    if (!template) {
      setTitle("");
      setDescription("");
      setAssigneeMemberIds([]);
      setSelectedCustomer(null);
      setLocationId(null);
      setPriority(TicketPriority.NONE);
      setTags("");
      setTasks([]);
      return;
    }

    const def = template.definition as TicketTemplateDefinition | undefined;
    const predefined = def?.predefined;

    setTitle((current) => (keep && current.trim() ? current : (predefined?.title ?? "")));
    setDescription((current) => (keep && current.trim() ? current : (predefined?.description ?? "")));
    setPriority((current) => (keep && current !== TicketPriority.NONE ? current : (predefined?.priority ?? TicketPriority.NONE)));
    const templateAssignees = (predefined?.assigneeMemberIds ?? []).filter((id): id is number => typeof id === "number");
    const templateLocationId = typeof predefined?.locationId === "number" ? predefined.locationId : null;
    setAssigneeMemberIds((current) => (keep && current.length > 0 ? current : templateAssignees));
    setLocationId((current) => (keep && current !== null ? current : templateLocationId));
    setTags((current) => (keep && current.trim() ? current : (predefined?.tags?.join(", ") ?? "")));

    const templateCustomer: SelectedCustomer | null =
      typeof predefined?.userId === "number"
        ? { id: predefined.userId, name: template.resolved?.user?.name ?? formatUserDisplayName(predefined.userId) }
        : null;
    setSelectedCustomer((current) => (keep && current !== null ? current : templateCustomer));

    const templateTasks = def?.tasks ?? [];
    const nextTasks = templateTasks.map((t, i) => ({
      id: `template-${i}-${Date.now()}`,
      description: t.shortDescription
    }));
    setTasks((current) => (keep && current.length > 0 ? current : nextTasks));
  };

  const handleTemplateChange = useCallback(
    (templateId: number | null): void => {
      if (templateId === selectedTemplateId) return;
      // An explicit pick replaces the whole form — ask first when there is something to lose.
      if (hasEntries && !window.confirm("Replace what you've entered with this template?")) return;
      setSelectedTemplateId(templateId);
      if (templateId === null) {
        applyTemplate(null, "replace");
      } else {
        const template = templates.find((t) => t.id === templateId);
        if (template) {
          applyTemplate(template, "replace");
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templates, selectedTemplateId, hasEntries]
  );

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!title.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      const tagList = tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      // Every field the form shows is sent as it stands — an empty one as null / [] — so the
      // server never re-fills from the template a field the agent cleared (D9). The server
      // keeps the template's value only for fields a caller does not send at all.
      const ticket = await createTicket({
        title: title.trim(),
        description: description.trim() || null,
        priority,
        tags: tagList,
        assigneeMemberIds,
        userId: selectedCustomer?.id ?? null,
        locationId: locationId ?? null,
        templateId: selectedTemplateId ?? undefined,
        // When set AND no explicit assignees, the ticket is dispatched to this
        // group's inbox (open-to-group). Explicit assignees override.
        routingGroupSlug: routingGroupSlug || undefined,
        // Always send the local task list — when a template is selected the
        // user may have edited or removed tasks before saving, so we send
        // exactly what's on screen rather than letting the backend re-derive
        // tasks from the template definition.
        tasks: tasks.map((t) => ({ shortDescription: t.description }))
      });

      onClose();
      // Let the caller move the inbox nav to wherever the ticket landed
      // (e.g. its group inbox) before we open the ticket detail.
      onCreated?.(ticket);
      navigate(`/inbox/ticket/${formatTicketNumberDisplay(ticket.number)}`);
    } catch (e: any) {
      setError(e?.message ?? "Failed to create ticket");
    } finally {
      setSubmitting(false);
    }
  }, [
    title,
    description,
    priority,
    tags,
    assigneeMemberIds,
    selectedCustomer,
    locationId,
    selectedTemplateId,
    tasks,
    routingGroupSlug,
    createTicket,
    onClose,
    onCreated,
    navigate
  ]);

  const selectedTemplate = selectedTemplateId ? (templates.find((t) => t.id === selectedTemplateId) ?? null) : null;

  // Members the draft's location rules out are listed disabled rather than
  // dropped (ENG-2961), so someone searching for a name finds them with the
  // reason attached. Server-side validation independently rejects
  // out-of-scope assignees on create (ENG-2882).
  const assigneeOptions = useMemo(
    () => buildAssigneeOptions({ members, orgId: Member.orgId, locationId, groups: groups ?? [] }),
    [members, locationId, groups]
  );

  // Drop selected assignees that lose access when the location changes
  // (covers template-predefined assignees too, mirroring the server's strip).
  // Skipped until members are loaded — we can't evaluate access before then.
  useEffect(() => {
    if (!members.length) return;
    setAssigneeMemberIds((prev) =>
      prev.filter((id) => {
        const member = members.find((m) => m.id === id);
        return member ? memberCanAccessTicketLocation(member, Member.orgId, locationId, groups ?? []) : false;
      })
    );
  }, [locationId, members, groups]);

  const locationOptions: SelectItem[] = useMemo(() => {
    const result: SelectItem[] = [{ label: "None", value: "" }, { type: "divider" }];

    if (!groups?.length) return result;

    groups.forEach((group, groupIndex) => {
      if (groupIndex > 0) {
        result.push({ type: "divider" });
      }
      result.push({
        type: "group",
        label: group.name,
        options: (group.locations ?? []).map((l) => ({ label: l.name, value: String(l.id) }))
      });
    });

    return result;
  }, [groups]);

  // Distinct, non-archived routing groups (a group may have rows at multiple
  // scopes — show each named team once), matching the detail-panel Group field.
  const groupOptions: SelectItem[] = useMemo(() => {
    const nameBySlug = new Map<string, string>();
    for (const g of routingGroups) {
      if (g.isArchived) continue;
      if (!nameBySlug.has(g.slug)) nameBySlug.set(g.slug, g.name);
    }
    const groupItems = Array.from(nameBySlug.entries()).map(([slug, name]) => ({ label: name, value: slug }));
    return [{ label: "No group", value: "" }, { type: "divider" }, ...groupItems];
  }, [routingGroups]);

  return {
    selectedTemplateId,
    title,
    setTitle,
    description,
    setDescription,
    assigneeMemberIds,
    setAssigneeMemberIds,
    locationId,
    setLocationId,
    priority,
    setPriority,
    selectedCustomer,
    setSelectedCustomer,
    tags,
    setTags,
    routingGroupSlug,
    setRoutingGroupSlug,
    tasks,
    setTasks,
    newTaskText,
    setNewTaskText,
    showTaskInput,
    setShowTaskInput,
    submitting,
    error,
    templates,
    templatesLoading,
    selectedTemplate,
    assigneeOptions,
    locationOptions,
    groupOptions,
    handleTemplateChange,
    handleSubmit
  };
}
